import { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2, ShoppingBag, Check, Minus, Plus, MapPin, Info, Trash2, AlertTriangle, PlusCircle, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ProductThumb } from '@/components/shop/ProductThumb';
import { useCreatePickupOrder } from '@/hooks/usePickupOrder';
import { useActivePickupWindows, useHasPickupWindows, formatPickupWindowLabel } from '@/hooks/usePickupWindows';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { useToast } from '@/hooks/use-toast';
import { track } from '@/lib/analytics';
import { toastFlowError } from '@/lib/toast/toastFlowError';
import { captureAppError } from '@/lib/monitoring/sentry';
import { logSystemEvent } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import { resolvePickupInstructions } from '@/lib/shop/pickup';
import { parseStockError } from './PickupOrderDialogHelpers';
import { useTranslate } from '@/i18n';

export interface PreorderProduct {
  id: string;
  title: string;
  price: number; // kronor
  currencyCode: string;
  imageUrl?: string | null;
  pickupInstructions?: string | null;
  stockLimit?: number | null; // null = unlimited
  remaining?: number | null;  // null = unlimited
}

interface OrderDraftItem {
  product: PreorderProduct;
  quantity: number;
}

interface Props {
  initialProduct: PreorderProduct;
  producerId: string;
  producerName: string;
  pickupInstructions?: string | null;
  sourceSurface?: string;
}

function formatPrice(amount: number, currency: string = 'SEK') {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function PickupOrderForm({ initialProduct, producerId, producerName, pickupInstructions, sourceSurface = 'product_page' }: Props) {
  const t = useTranslate();
  const { toast } = useToast();
  const mutation = useCreatePickupOrder();

  const [items, setItems] = useState<OrderDraftItem[]>([{ product: initialProduct, quantity: 1 }]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [stockWarning, setStockWarning] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedWindowId, setSelectedWindowId] = useState<string>('');
  const requestIdRef = useRef(crypto.randomUUID());

  const { data: pickupWindows = [] } = useActivePickupWindows(producerId);
  const { data: hasAnyWindows = false } = useHasPickupWindows(producerId);
  const allWindowsExpired = hasAnyWindows && pickupWindows.length === 0;

  const getMaxQuantity = (p: PreorderProduct): number => {
    if (p.remaining !== null && p.remaining !== undefined) return Math.max(0, p.remaining);
    if (p.stockLimit !== null && p.stockLimit !== undefined) return Math.max(0, p.stockLimit);
    return 99;
  };

  const updateQuantity = useCallback((productId: string, delta: number) => {
    setItems(prev => prev.map(item => {
      if (item.product.id !== productId) return item;
      const max = getMaxQuantity(item.product);
      const newQty = Math.max(1, Math.min(max, item.quantity + delta));
      return { ...item, quantity: newQty };
    }));
    setStockWarning(null);
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems(prev => prev.filter(item => item.product.id !== productId));
  }, []);

  const addProduct = useCallback((p: PreorderProduct) => {
    setItems(prev => {
      const existing = prev.find(item => item.product.id === p.id);
      if (existing) {
        const max = getMaxQuantity(p);
        return prev.map(item =>
          item.product.id === p.id
            ? { ...item, quantity: Math.min(max, item.quantity + 1) }
            : item
        );
      }
      const max = getMaxQuantity(p);
      if (max <= 0) return prev;
      return [...prev, { product: p, quantity: 1 }];
    });
    setPickerOpen(false);
  }, []);

  const totalPrice = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const currency = items[0]?.product.currencyCode || 'SEK';

  const coverMap = useProductCoverImages(items.map(i => i.product.id)).data ?? {};

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setStockWarning(null);

    if (!email || email.length < 3) {
      toastFlowError('E-post krävs', 'Ange en giltig e-postadress.');
      return;
    }

    if (items.length === 0) return;

    track('pickup_order_started', {
      producer_id: producerId,
      product_ids: items.map(i => i.product.id),
      qty_total: items.reduce((s, i) => s + i.quantity, 0),
    });

    try {
      const result = await mutation.mutateAsync({
        producer_id: producerId,
        items: items.map(i => ({ product_id: i.product.id, quantity: i.quantity })),
        customer_email: email.trim(),
        customer_name: name.trim() || undefined,
        customer_phone: phone.trim() || undefined,
        customer_note: note.trim() || undefined,
        client_request_id: requestIdRef.current,
        pickup_window_id: selectedWindowId || undefined,
      });

      track('pickup_order_completed', {
        order_id: result.order_id,
        producer_id: producerId,
        total_amount: result.total_amount,
      });

      setOrderNumber(result.order_number);
      setSubmitted(true);
    } catch (err: any) {
      const msg = err.message || '';
      const stockItems = parseStockError(msg);
      if (stockItems) {
        setItems(prev => prev.map(item => {
          const conflict = stockItems.find(s => s.product_id === item.product.id);
          if (!conflict) return item;
          return {
            ...item,
            quantity: Math.max(0, conflict.available_stock),
            product: { ...item.product, remaining: conflict.available_stock },
          };
        }).filter(item => item.quantity > 0));

        setStockWarning('Vi justerade antalet utifrån aktuellt lager.');
        toast({
          title: 'Antalet uppdaterades utifrån lager',
          description: 'Kontrollera beställningen och skicka igen.',
        });
        requestIdRef.current = crypto.randomUUID();
        return;
      }

      if (msg.includes('finns bara') || msg.includes('inga fler kvar')) {
        setStockWarning(msg);
        toast({
          title: 'Lagret räckte inte',
          description: 'Justera antalet och försök igen.',
        });
        requestIdRef.current = crypto.randomUUID();
        return;
      }

      console.error('[PickupOrderForm] RPC failed:', msg);
      captureAppError(err, {
        feature: 'shop',
        action: 'create_pickup_order',
        producerId,
        requestId: requestIdRef.current,
      });
      logSystemEvent({
        level: 'error',
        event_key: 'pickup_order_rpc_failed',
        domain: 'shop',
        entity_type: 'order',
        producer_id: producerId,
        message: msg || 'Unknown RPC error',
        request_id: requestIdRef.current,
        context: buildSystemEventContext({ feature: 'shop', action: 'create_pickup_order', surface: 'pickup_order_form', rpc_name: 'create_pickup_order', product_ids: items.map(i => i.product.id) }),
      });
      toastFlowError('Kunde inte skapa beställning');
    }
  };

  if (submitted) {
    return (
      <div className="py-6 space-y-5 max-w-2xl mx-auto">
        <div className="text-center space-y-3">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Check className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">{t('order.thankYou')}</h3>
        </div>

        <div className="rounded-lg bg-muted/50 border border-border p-4 space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Ordernummer</span>
            <span className="font-mono font-medium text-foreground">{orderNumber}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Värd</span>
            <span className="font-medium text-foreground">{producerName}</span>
          </div>
          <div className="border-t border-border" />
          <div className="space-y-1.5">
            <span className="text-muted-foreground">Du beställde:</span>
            {items.map(item => (
              <div key={item.product.id} className="flex justify-between items-center pl-2">
                <span className="text-foreground truncate mr-2">{item.product.title}</span>
                <span className="text-muted-foreground shrink-0">×{item.quantity}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border" />
          <div className="flex justify-between items-center font-medium">
            <span className="text-foreground">Totalt</span>
            <span className="text-foreground">{formatPrice(totalPrice, currency)}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground text-center max-w-xs mx-auto leading-relaxed">
          {producerName} hör av sig med information om upphämtning. En bekräftelse har skickats till din e-post.
        </p>

      </div>
    );
  }

  return (
    <form onSubmit={(e) => handleSubmit(e)} className="space-y-4 mt-2">
      {stockWarning && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">{stockWarning}</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item) => {
          const max = getMaxQuantity(item.product);
          const isLimited = item.product.remaining !== null && item.product.remaining !== undefined;
          return (
            <div key={item.product.id} className="rounded-lg bg-muted/50 border border-border p-3">
              <div className="flex items-center gap-3">
                <ProductThumb
                  src={coverMap?.[item.product.id] ?? item.product.imageUrl}
                  alt={item.product.title}
                  title={item.product.title}
                  size="md"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground text-sm truncate">{item.product.title}</p>
                  <p className="text-xs text-muted-foreground">{formatPrice(item.product.price, item.product.currencyCode)} / st</p>
                </div>
                {items.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0" onClick={() => removeItem(item.product.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product.id, -1)} disabled={item.quantity <= 1}><Minus className="h-3 w-3" /></Button>
                  <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                  <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product.id, 1)} disabled={item.quantity >= max}><Plus className="h-3 w-3" /></Button>
                </div>
                <span className="text-sm font-medium text-foreground">{formatPrice(item.product.price * item.quantity, item.product.currencyCode)}</span>
              </div>

              {isLimited && (
                <p className="text-xs text-muted-foreground mt-1.5">{item.product.remaining === 0 ? t('status.soldOut') : t('order.label.remainingToReserve', { count: item.product.remaining as number })}</p>
              )}
            </div>
          );
        })}
      </div>

      <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => setPickerOpen(true)}>
        <PlusCircle className="h-4 w-4" />
        {t('order.cta.addMore')}
      </Button>

      <div className="flex justify-between text-sm pt-1 border-t border-border">
        <span className="text-muted-foreground">Totalt ({items.reduce((s, i) => s + i.quantity, 0)} varor)</span>
        <span className="font-semibold text-foreground">{formatPrice(totalPrice, currency)}</span>
      </div>

      {pickupWindows.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-muted-foreground" /> Välj upphämtning *</Label>
          <RadioGroup value={selectedWindowId} onValueChange={setSelectedWindowId}>
            {pickupWindows.map(w => (
              <div key={w.id} className="flex items-center gap-2">
                <RadioGroupItem value={w.id} id={`pw-${w.id}`} />
                <Label htmlFor={`pw-${w.id}`} className="text-sm font-normal cursor-pointer">{formatPickupWindowLabel(w)}</Label>
              </div>
            ))}
          </RadioGroup>
          {pickupWindows.length > 0 && !selectedWindowId && (
            <p className="text-xs text-amber-600">Välj en upphämtningstid för att fortsätta.</p>
          )}
        </div>
      )}

      {allWindowsExpired && (
        <div className="rounded-lg bg-muted/50 border border-border p-3 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Clock className="h-4 w-4 shrink-0" /> Inga bokningsbara upphämtningstider just nu</div>
          <p className="text-xs text-muted-foreground pl-6">Värden har inte lagt till nya tider ännu. Kom gärna tillbaka senare.</p>
        </div>
      )}

      <div className="rounded-lg bg-muted/50 border border-border p-3 space-y-1.5">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground"><MapPin className="h-4 w-4 text-muted-foreground shrink-0" /> Hämtas på gården</div>
        <p className="text-xs text-muted-foreground pl-6">{resolvePickupInstructions({ productInstructions: items[0]?.product.pickupInstructions, producerInstructions: pickupInstructions }).text}</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="pickup-email">E-post *</Label>
        <Input id="pickup-email" type="email" required placeholder="din@epost.se" value={email} onChange={e => setEmail(e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="pickup-name">Namn <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
        <Input id="pickup-name" placeholder="Ditt namn" value={name} onChange={e => setName(e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="pickup-phone">Telefon <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
        <Input id="pickup-phone" type="tel" placeholder="070-123 45 67" value={phone} onChange={e => setPhone(e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="pickup-note">Meddelande <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
        <Textarea id="pickup-note" placeholder="Berätta gärna om du har särskilda önskemål..." rows={2} value={note} onChange={e => setNote(e.target.value)} />
      </div>

      <p className="text-xs text-muted-foreground">Ingen betalning sker nu. Värden kontaktar dig med information om upphämtning.</p>

      <Button type="submit" className="w-full gap-2" disabled={mutation.isPending || !email || items.length === 0 || allWindowsExpired || (pickupWindows.length > 0 && !selectedWindowId)}>
        {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
        {mutation.isPending ? 'Skickar beställning...' : 'Skicka beställning'}
      </Button>
    </form>
  );
}
